import React from "react";
import { clsx } from "clsx";
import typographyStyles from "@bloomberg/media-ui-tokens/css/typography.module.css";

import ItemsWithHeadshots from "~components/shared/items-with-headshots/ItemsWithHeadshots";
import {
    type MediaConfig,
    Media,
    MediaFormat,
    DEFAULT_MEDIA_PRIORITY,
    getWinningMediaFormat,
} from "~components/shared/media";
import { StoryBlock } from "~components/shared/story-block/StoryBlock";
import Title from "~components/shared/title/Title";
import { VariantBase } from "~components/shared/variant-base/VariantBase";
import {
    type VariationComponent,
    ZoneColumnCombo,
    getIsOpinionPageTheme,
} from "~types/types";
import { integrityError } from "~utils/moduleIntegrity";

import integrityCheck from "./LineupContentOpinion.integrity";
import { PREVIEW_NOTES } from "./previewNotes";
import styles from "./LineupContentOpinion.module.css";

export const MEDIA_PRIORITY: MediaFormat[] = [
    MediaFormat.verticalVideo,
    ...DEFAULT_MEDIA_PRIORITY,
];
const MEDIA_CONFIG: MediaConfig = {
    aspectRatio: 3 / 2,
    className: styles.media,
    desktopMaxWidth: 353,
    mobileMaxWidth: 715,
    showCaption: false,
};

const SUPPORTED_REGIONS: ZoneColumnCombo[] = [ZoneColumnCombo.leftContentWell];

export default function LineupContentOpinion(props: VariationComponent) {
    const { items, labelUrl, theme } = props;

    const integrity = integrityCheck(props);

    if (integrity.length) return integrityError(integrity);

    const [primaryStory, ...secondaryStories] = items;
    const hasSecondaryStories = Boolean(secondaryStories?.length);
    const isVerticalVideo =
        getWinningMediaFormat(primaryStory, MEDIA_PRIORITY) ===
        MediaFormat.verticalVideo;

    // show if not vertical video story type or if vertical video decoration override
    const showStoryBlock =
        !isVerticalVideo || primaryStory.videoCurationOverride;

    return (
        <VariantBase
            source={props}
            className={styles.LineupContentOpinion}
            gridClassName={clsx(
                styles.opinion,
                getIsOpinionPageTheme(theme) && styles.isOpinionTheme
            )}
            supportedRegions={SUPPORTED_REGIONS}
            previewNotes={PREVIEW_NOTES}
        >
            <Title
                className={clsx(styles.title, typographyStyles.coreHeadlineXxs)}
                title="Bloomberg Opinion"
                titleUrl={labelUrl}
            />
            <div className={styles.primaryStory}>
                <Media
                    source={primaryStory}
                    priority={MEDIA_PRIORITY}
                    config={MEDIA_CONFIG}
                />
                {showStoryBlock && (
                    <StoryBlock
                        storyIndex={1}
                        withDek={Boolean(primaryStory.summary)}
                        story={primaryStory}
                        isBylineEligible
                        withOptionalEyebrow={!getIsOpinionPageTheme(theme)}
                    />
                )}
            </div>
            {hasSecondaryStories && (
                <ItemsWithHeadshots items={secondaryStories} isOpinion />
            )}
        </VariantBase>
    );
}
